import { Injectable, NgZone } from '@angular/core';
import Keycloak, {
    KeycloakConfig,
    KeycloakInitOptions,
    KeycloakProfile,
} from 'keycloak-js';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private kc!: Keycloak;
    private initOnce?: Promise<boolean>;        // ⬅️ idempotent init
    public ready = false;                       // init gotov
    private loginInProgress = false;
    private tokenRefreshIntervalId?: number;
    private readonly tokenMinValiditySeconds = 300;

    // emitira trenutno auth stanje (true/false)
    public authChanged = new BehaviorSubject<boolean>(false);
    // emitira labelu korisnika za prikaz u headeru
    public userLabel$ = new BehaviorSubject<string | null>(null);

    constructor(private zone: NgZone) {
        // kreiraj KC instancu jednom
        const cfg: KeycloakConfig = {
            url: environment.keycloak.url,
            realm: environment.keycloak.realm,
            clientId: environment.keycloak.clientId,
        };
        this.kc = new Keycloak(cfg);
    }

    async init(): Promise<boolean> {
        if (this.initOnce) return this.initOnce;  // ⬅️ spriječi 2. poziv

        const base = document.querySelector('base')?.getAttribute('href') ?? '/';
        const silentUri = `${window.location.origin}${base}assets/silent-check-sso.html`;
        const initOptions: KeycloakInitOptions = {
            onLoad: 'check-sso',
            pkceMethod: 'S256',
            checkLoginIframe: false,
            silentCheckSsoRedirectUri: silentUri,
        };

        // hookovi -> uvijek kroz emitAuth
        this.kc.onAuthSuccess = () => this.emitAuth(true);
        this.kc.onAuthLogout = () => this.emitAuth(false);
        this.kc.onTokenExpired = async () => {
            try {
                await this.refreshToken();
                this.emitAuth(this.kc.authenticated === true);
            } catch {
                this.handleTokenRefreshFailure();
            }
        };

        this.initOnce = (async () => {
            try {
                const authenticated = await this.kc.init(initOptions);
                this.zone.run(() => { this.ready = true; });
                this.emitAuth(authenticated);
                if (authenticated) this.setupTokenRefresh();
                return authenticated;
            } catch (e) {
                console.error('[KC] init error', e);
                this.zone.run(() => { this.ready = true; });   // ⬅️ i u catchu
                this.emitAuth(false);
                return false;
            }
        })();

        return this.initOnce;
    }

    private emitAuth(isAuth: boolean) {
        this.zone.run(() => {
            this.authChanged.next(isAuth);
            this.userLabel$.next(this.computeUserLabel());
        });
    }

    /** Sigurna provjera — ne baca grešku ako kc nije spreman. */
    isAuthenticated(): boolean {
        return this.kc?.authenticated === true;
    }

    async ensureReady(): Promise<void> {
        await this.init();                         // ⬅️ koristi jedini init
    }

    /** Vraća važeći token; pokuša ga prvo osvježiti (min 30s). */
    async getToken(): Promise<string | null> {
        if (!this.isAuthenticated()) return null;
        try {
            await this.refreshToken();
            return this.kc.token ?? null;
        } catch {
            this.handleTokenRefreshFailure();
            return null;
        }
    }

    async login(redirectUrl?: string) {
        if (this.loginInProgress) return;
        this.loginInProgress = true;
        try {
            await this.kc?.login({
                redirectUri: redirectUrl ? window.location.origin + redirectUrl : undefined,
            });
        } catch (e) {
            this.loginInProgress = false;
            throw e;
        }
    }

    resetPassword(redirectUrl = '/login'): void {
        const realm = encodeURIComponent(environment.keycloak.realm);
        const clientId = encodeURIComponent(environment.keycloak.clientId);
        const redirectUri = encodeURIComponent(`${window.location.origin}${redirectUrl}`);
        const resetUrl = `${environment.keycloak.url}/realms/${realm}/login-actions/reset-credentials?client_id=${clientId}&redirect_uri=${redirectUri}`;
        window.location.assign(resetUrl);
    }

    logout(): void {
        this.kc?.logout({ redirectUri: window.location.origin });
    }

    /** Labela za header: preferira ime/prezime, potom username/email. */
    private computeUserLabel(): string | null {
        const tp: any = this.kc?.tokenParsed ?? {};
        const fullName =
            tp?.name ||
            (tp?.given_name && tp?.family_name
                ? `${tp.given_name} ${tp.family_name}`
                : null);
        const username = tp?.preferred_username || tp?.email || null;
        return (fullName || username) ?? null;
    }

    updateUserLabel(firstName?: string | null, lastName?: string | null): void {
        const label = [firstName, lastName]
            .map(value => (value ?? '').trim())
            .filter(Boolean)
            .join(' ');

        this.userLabel$.next(label || this.computeUserLabel());
    }

    async loadProfile(): Promise<KeycloakProfile | null> {
        if (!this.isAuthenticated()) return null;
        try {
            const prof = await this.kc.loadUserProfile();
            // ažuriraj labelu nakon profila
            const label =
                prof.firstName && prof.lastName
                    ? `${prof.firstName} ${prof.lastName}`
                    : prof.username || prof.email || this.computeUserLabel();
            this.userLabel$.next(label ?? null);
            return prof;
        } catch {
            return null;
        }
    }

    /** Periodički refresh tokena kao dodatna sigurnost (fallback). */
    private setupTokenRefresh(): void {
        if (this.tokenRefreshIntervalId != null) return;

        const refreshIfAuthenticated = async () => {
            if (!this.kc?.authenticated) return;
            try {
                await this.refreshToken();
            } catch {
                this.handleTokenRefreshFailure();
            }
        };

        this.tokenRefreshIntervalId = window.setInterval(refreshIfAuthenticated, 60_000);
        window.addEventListener('focus', refreshIfAuthenticated);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                void refreshIfAuthenticated();
            }
        });
    }

    private refreshToken(): Promise<boolean> {
        return this.kc.updateToken(this.tokenMinValiditySeconds);
    }

    private handleTokenRefreshFailure(): void {
        try {
            this.kc?.clearToken();
        } catch {
            // Keycloak clearToken moze baciti ako je instanca u rubnom stanju.
        }
        this.emitAuth(false);
    }

    // ---- Roles helpers (realm + client roles) ----
    getRealmRoles(): string[] {
        const tp: any = this.kc?.tokenParsed ?? {};
        const realmRoles: string[] = tp?.realm_access?.roles ?? [];
        const clientRoles: string[] = Object.values(tp?.resource_access ?? {})
            .flatMap((ra: any) => ra?.roles ?? []);
        return Array.from(new Set([...realmRoles, ...clientRoles]));
    }

    hasAllRoles(roles: string[]): boolean {
        const mine = this.getRealmRoles();
        return roles.every(r => mine.includes(r));
    }

    hasRole(role: string): boolean {
        return this.getRealmRoles().includes(role);
    }

    hasAnyRole(roles: string[]): boolean {
        const have = this.getRealmRoles();
        return roles.some((r) => have.includes(r));
    }
}
